import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Search, Filter, CheckCircle, Clock, DollarSign,
  Users, CreditCard, X, FileText, AlertCircle, Edit, RotateCcw, History, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api from '../api';

const CURRENCIES = ['INR', 'USD', 'AED', 'CAD', 'GBP', 'EUR', 'SGD', 'AUD'];
const PAYMENT_METHODS = ['PayPal', 'Razorpay', 'Credit/Debit Card', 'UPI', 'Zelle', 'Bank Transfer', 'Cash', 'Cheque'];
const FX_RATES = { INR: 1, USD: 83.5, CAD: 62, GBP: 105, EUR: 90, AED: 22.7, SGD: 62, AUD: 54 };
const VERTICALS = ['Coding', 'AI', 'Robotics', 'Mathematics', 'Spoken Mastery', 'PTE', 'IELTS'];
const PROGRAMS = {
  'Coding': ['Tech Sprout', 'Tech Titan'],
  'AI': ['AI Explorer'],
  'Robotics': ['Robo Builder'],
  'Mathematics': ['Math Whiz'],
  'Spoken Mastery': ['Fluency Pro'],
  'PTE': ['PTE Prep'],
  'IELTS': ['IELTS Prep'],
};

const COUNTRY_TIMEZONES = {
  'India': ['Asia/Kolkata'],
  'United States': ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu'],
  'Canada': ['America/Toronto', 'America/Winnipeg', 'America/Edmonton', 'America/Vancouver', 'America/Halifax'],
  'United Kingdom': ['Europe/London'],
  'Australia': ['Australia/Sydney', 'Australia/Melbourne', 'Australia/Brisbane', 'Australia/Perth', 'Australia/Adelaide'],
  'UAE': ['Asia/Dubai'],
  'Singapore': ['Asia/Singapore'],
  'Germany': ['Europe/Berlin'],
  'France': ['Europe/Paris'],
  'Japan': ['Asia/Tokyo'],
  'South Korea': ['Asia/Seoul'],
  'Saudi Arabia': ['Asia/Riyadh'],
  'Qatar': ['Asia/Qatar'],
  'Oman': ['Asia/Muscat'],
  'Kuwait': ['Asia/Kuwait'],
  'Bahrain': ['Asia/Bahrain'],
  'New Zealand': ['Pacific/Auckland'],
  'South Africa': ['Africa/Johannesburg'],
  'Malaysia': ['Asia/Kuala_Lumpur'],
  'Philippines': ['Asia/Manila'],
  'Indonesia': ['Asia/Jakarta'],
  'Thailand': ['Asia/Bangkok'],
  'Nigeria': ['Africa/Lagos'],
  'Kenya': ['Africa/Nairobi'],
  'Egypt': ['Africa/Cairo'],
  'Israel': ['Asia/Jerusalem'],
  'Ireland': ['Europe/Dublin'],
  'Netherlands': ['Europe/Amsterdam'],
  'Spain': ['Europe/Madrid'],
  'Italy': ['Europe/Rome'],
  'Sweden': ['Europe/Stockholm'],
  'Switzerland': ['Europe/Zurich'],
  'China': ['Asia/Shanghai'],
  'Hong Kong': ['Asia/Hong_Kong'],
  'Taiwan': ['Asia/Taipei'],
  'Brazil': ['America/Sao_Paulo'],
  'Mexico': ['America/Mexico_City'],
  'Other': [],
};

const COUNTRIES = Object.keys(COUNTRY_TIMEZONES);

const EnrollmentManager = () => {
  const [enrollments, setEnrollments] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [saving, setSaving] = useState(false);

  // Edit state
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ num_classes: '', amount: '', currency: '', payment_method: '', transaction_ref: '', reason: '' });

  // Refund state
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundTarget, setRefundTarget] = useState(null);
  const [refundForm, setRefundForm] = useState({ refund_type: 'full', refund_amount: '', reason: '' });

  // Audit log state
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditTarget, setAuditTarget] = useState(null);
  const [generatingInvoice, setGeneratingInvoice] = useState(null);

  // Create form
  const [form, setForm] = useState({
    student_id: '', enrollment_type: 'fresh', num_classes: '',
    currency: 'INR', amount: '', payment_method: '',
    country: '', timezone: '', new_student_name: '', new_student_email: '',
    parent_name: '', contact_number: '', vertical: '', program: '',
    transaction_ref: '', payment_date: new Date().toISOString().split('T')[0],
  });

  // Demos for "New from Demo"
  const [demos, setDemos] = useState([]);

  // Pay form
  const [payForm, setPayForm] = useState({ transaction_ref: '', payment_date: '' });

  const fetchEnrollments = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (searchQuery) params.append('search', searchQuery);
      const res = await api.get(`/admin/enrollments?${params}`);
      setEnrollments(res.data || []);
    } catch { toast.error('Failed to load enrollments'); }
    setLoading(false);
  }, [filterStatus, searchQuery]);

  const fetchStudents = useCallback(async () => {
    try {
      const res = await api.get('/admin/students-list');
      setStudents(res.data || []);
    } catch { /* non-critical */ }
  }, []);

  const fetchDemos = useCallback(async () => {
    try {
      const res = await api.get('/admin/demos?status=completed');
      setDemos(res.data?.demos || []);
    } catch { /* non-critical */ }
  }, []);

  useEffect(() => { fetchEnrollments(); fetchStudents(); fetchDemos(); }, [fetchEnrollments, fetchStudents, fetchDemos]);

  const selectedStudent = students.find(s => s.id === form.student_id);

  // When enrollment type or student changes in renewal mode, auto-populate
  const handleEnrollmentTypeChange = (type) => {
    setForm(prev => ({
      ...prev,
      enrollment_type: type,
      student_id: '',
      country: '',
      timezone: '',
      new_student_name: '',
      new_student_email: '',
    }));
  };

  const handleStudentSelect = (studentId) => {
    if (studentId === '__other__') {
      setForm(prev => ({ ...prev, student_id: '__other__', new_student_name: '', new_student_email: '' }));
      return;
    }
    const student = students.find(s => s.id === studentId);
    if (form.enrollment_type === 'renewal' && student) {
      setForm(prev => ({
        ...prev,
        student_id: studentId,
        country: student.country || student.region || prev.country,
        timezone: student.timezone || prev.timezone,
        currency: student.country === 'India' ? 'INR' : student.country === 'United States' ? 'USD' : student.country === 'Canada' ? 'CAD' : student.country === 'United Kingdom' ? 'GBP' : prev.currency,
      }));
    } else {
      setForm(prev => ({ ...prev, student_id: studentId }));
    }
  };

  const handleCountryChange = (country) => {
    const tzs = COUNTRY_TIMEZONES[country] || [];
    setForm(prev => ({
      ...prev,
      country,
      timezone: tzs.length === 1 ? tzs[0] : '',
      currency: country === 'India' ? 'INR' : country === 'United States' ? 'USD' : country === 'Canada' ? 'CAD' : country === 'United Kingdom' ? 'GBP' : country === 'UAE' ? 'INR' : prev.currency,
    }));
  };

  const availableTimezones = COUNTRY_TIMEZONES[form.country] || [];

  const handleCreate = async () => {
    if (form.student_id === '__other__') {
      if (!form.new_student_name.trim()) { toast.error('Enter the student name'); return; }
    } else if (!form.student_id) {
      toast.error('Select a student or choose "Other"'); return;
    }
    if (!form.num_classes || parseInt(form.num_classes) <= 0) { toast.error('Enter valid class count'); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error('Enter valid amount'); return; }
    if (!form.payment_method) { toast.error('Select payment method'); return; }
    if (!form.transaction_ref.trim()) { toast.error('Enter transaction ID'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        num_classes: parseInt(form.num_classes),
        amount: parseFloat(form.amount),
      };
      if (form.student_id === '__other__') {
        payload.student_id = '';
        payload.new_student_name = form.new_student_name.trim();
        payload.new_student_email = form.new_student_email.trim();
      }
      // Create enrollment AND auto-mark as paid in one step
      const res = await api.post('/admin/enrollments', payload);
      const enrollmentId = res.data.id;
      // Auto mark-paid since we have all payment details
      try {
        await api.put(`/admin/enrollments/${enrollmentId}/mark-paid`, {
          transaction_ref: form.transaction_ref.trim(),
          payment_date: form.payment_date || new Date().toISOString().split('T')[0],
        });
        toast.success('Enrollment created & payment recorded!');
      } catch {
        toast.success('Enrollment created (mark as paid manually)');
      }
      setCreateOpen(false);
      setForm({ student_id: '', enrollment_type: 'fresh', num_classes: '', currency: 'INR', amount: '', payment_method: '', country: '', timezone: '', new_student_name: '', new_student_email: '', parent_name: '', contact_number: '', vertical: '', program: '', transaction_ref: '', payment_date: new Date().toISOString().split('T')[0] });
      fetchEnrollments();
      fetchStudents();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  const openMarkPaid = (enrollment) => {
    setPayTarget(enrollment);
    setPayForm({ transaction_ref: '', payment_date: new Date().toISOString().split('T')[0] });
    setPayOpen(true);
  };

  const handleMarkPaid = async () => {
    if (!payForm.transaction_ref.trim()) { toast.error('Enter transaction reference'); return; }
    if (!payForm.payment_date) { toast.error('Enter payment date'); return; }
    setSaving(true);
    try {
      await api.put(`/admin/enrollments/${payTarget.id}/mark-paid`, payForm);
      toast.success('Enrollment marked as paid. Classes credited!');
      setPayOpen(false);
      fetchEnrollments();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this pending enrollment?')) return;
    try {
      await api.delete(`/admin/enrollments/${id}`);
      toast.success('Enrollment deleted');
      fetchEnrollments();
    } catch (e) { toast.error(e.response?.data?.detail || 'Cannot delete'); }
  };

  // ── Edit Handlers ──
  const openEdit = (enrollment) => {
    setEditTarget(enrollment);
    setEditForm({
      num_classes: enrollment.num_classes || '',
      amount: enrollment.amount || '',
      currency: enrollment.currency || 'INR',
      payment_method: enrollment.payment_method || '',
      transaction_ref: enrollment.transaction_ref || '',
      reason: '',
    });
    setEditOpen(true);
  };

  const handleEdit = async () => {
    if (!editForm.reason.trim()) { toast.error('Please provide a reason for the edit'); return; }
    setSaving(true);
    try {
      await api.put(`/admin/enrollments/${editTarget.id}/edit`, {
        num_classes: parseInt(editForm.num_classes),
        amount: parseFloat(editForm.amount),
        currency: editForm.currency,
        payment_method: editForm.payment_method,
        transaction_ref: editForm.transaction_ref,
        reason: editForm.reason,
      });
      toast.success('Enrollment updated');
      setEditOpen(false);
      fetchEnrollments();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
    setSaving(false);
  };

  // ── Refund Handlers ──
  const openRefund = (enrollment) => {
    setRefundTarget(enrollment);
    setRefundForm({ refund_type: 'full', refund_amount: '', reason: '' });
    setRefundOpen(true);
  };

  const handleRefund = async () => {
    if (!refundForm.reason.trim()) { toast.error('Please provide a reason for the refund'); return; }
    if (refundForm.refund_type === 'partial' && (!refundForm.refund_amount || parseFloat(refundForm.refund_amount) <= 0)) {
      toast.error('Enter a valid refund amount'); return;
    }
    const confirmMsg = refundForm.refund_type === 'full'
      ? `Process FULL refund of ₹${(refundTarget.amount_inr || refundTarget.amount)?.toLocaleString()} for ${refundTarget.student_name}? This will deduct ${refundTarget.num_classes} class credits.`
      : `Process partial refund of ${refundTarget.currency} ${parseFloat(refundForm.refund_amount).toLocaleString()} for ${refundTarget.student_name}?`;
    if (!window.confirm(confirmMsg)) return;

    setSaving(true);
    try {
      const res = await api.post(`/admin/enrollments/${refundTarget.id}/refund`, {
        refund_type: refundForm.refund_type,
        refund_amount: parseFloat(refundForm.refund_amount || 0),
        reason: refundForm.reason,
      });
      toast.success(`${res.data.message} — ₹${res.data.refund_amount_inr?.toLocaleString()} refunded, ${res.data.classes_deducted} classes deducted`);
      setRefundOpen(false);
      fetchEnrollments();
    } catch (e) { toast.error(e.response?.data?.detail || 'Refund failed'); }
    setSaving(false);
  };

  // ── Audit Log ──
  const openAuditLog = async (enrollment) => {
    setAuditTarget(enrollment);
    try {
      const res = await api.get(`/admin/audit-logs?entity_id=${enrollment.id}&entity_type=enrollment`);
      setAuditLogs(res.data || []);
    } catch { setAuditLogs([]); }
    setAuditOpen(true);
  };

  const handleGenerateInvoice = async (enrollment) => {
    setGeneratingInvoice(enrollment.id);
    try {
      // Generate invoice if not exists, get invoice data back
      const res = await api.post('/invoices/generate', {
        payment_id: enrollment.id,
        student_id: enrollment.student_id,
        student_name: enrollment.student_name,
        payment_received: enrollment.amount_inr || enrollment.amount || 0,
        currency: enrollment.currency || 'INR',
        num_classes: enrollment.num_classes || 0,
        payment_method: enrollment.payment_method || '',
        transaction_ref: enrollment.transaction_ref || '',
        payment_date: enrollment.payment_date || '',
        enrollment_type: enrollment.enrollment_type || 'fresh',
      });
      const invoiceId = res.data?.id;
      if (invoiceId) {
        // Open PDF in new tab instantly
        const pdfUrl = `${api.defaults.baseURL}/invoices/${invoiceId}/pdf`;
        window.open(pdfUrl, '_blank');
      }
      toast.success('Invoice generated!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate invoice');
    }
    setGeneratingInvoice(null);
  };

  const pendingCount = enrollments.filter(e => e.status === 'pending').length;
  const paidCount = enrollments.filter(e => e.status === 'paid' || e.status === 'partial_refund').length;
  const refundedCount = enrollments.filter(e => e.status === 'refunded').length;
  const totalRevenue = enrollments.filter(e => e.status === 'paid' || e.status === 'partial_refund').reduce((s, e) => s + ((e.amount_inr || e.amount || 0) - (e.refunded_inr || 0)), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600" />
      </div>
    );
  }

  const getStatusBadge = (e) => {
    if (e.status === 'refunded') return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold">
        <RotateCcw className="w-3 h-3" /> Refunded
      </span>
    );
    if (e.status === 'partial_refund' || e.refund_status === 'partial_refund') return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 text-[10px] font-bold">
        <RotateCcw className="w-3 h-3" /> Partial Refund
      </span>
    );
    if (e.status === 'paid') return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold">
        <CheckCircle className="w-3 h-3" /> Paid
      </span>
    );
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold">
        <Clock className="w-3 h-3" /> Pending
      </span>
    );
  };

  return (
    <div className="space-y-6" data-testid="enrollment-manager">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="enrollment-title">Enrollments</h1>
          <p className="text-sm text-slate-500 mt-0.5">{enrollments.length} total enrollments</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="bg-blue-600 hover:bg-blue-700 font-bold text-sm" data-testid="new-enrollment-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New Enrollment
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center"><Clock className="w-5 h-5 text-amber-600" /></div>
            <div><p className="text-2xl font-black text-slate-900">{pendingCount}</p><p className="text-xs text-slate-500">Pending</p></div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center"><CheckCircle className="w-5 h-5 text-emerald-600" /></div>
            <div><p className="text-2xl font-black text-slate-900">{paidCount}</p><p className="text-xs text-slate-500">Paid</p></div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center"><RotateCcw className="w-5 h-5 text-red-600" /></div>
            <div><p className="text-2xl font-black text-slate-900">{refundedCount}</p><p className="text-xs text-slate-500">Refunded</p></div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center"><DollarSign className="w-5 h-5 text-blue-600" /></div>
            <div><p className="text-2xl font-black text-slate-900">₹{totalRevenue.toLocaleString()}</p><p className="text-xs text-slate-500">Net Revenue (INR)</p></div>
          </div>
        </div>
      </div>

      {/* Filters + Search */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by name or CITT ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="enrollment-search"
          />
        </div>
        <div className="flex items-center gap-1">
          <Filter className="w-4 h-4 text-slate-400" />
          {['all', 'pending', 'paid'].map(s => (
            <Button key={s} size="sm" variant={filterStatus === s ? 'default' : 'outline'}
              onClick={() => setFilterStatus(s)}
              className={`text-xs font-bold capitalize ${filterStatus === s ? 'bg-slate-800' : ''}`}
              data-testid={`filter-${s}`}>
              {s}
            </Button>
          ))}
        </div>
      </div>

      {/* Enrollment Table */}
      {enrollments.length === 0 ? (
        <div className="bg-white rounded-xl p-10 text-center border border-slate-200">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No enrollments found</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs font-bold">Student</TableHead>
                <TableHead className="text-xs font-bold">CITT ID</TableHead>
                <TableHead className="text-xs font-bold">Type</TableHead>
                <TableHead className="text-xs font-bold text-center">Classes</TableHead>
                <TableHead className="text-xs font-bold text-right">Amount</TableHead>
                <TableHead className="text-xs font-bold">Payment</TableHead>
                <TableHead className="text-xs font-bold text-center">Rate</TableHead>
                <TableHead className="text-xs font-bold text-right">INR Value</TableHead>
                <TableHead className="text-xs font-bold text-center">Status</TableHead>
                <TableHead className="text-xs font-bold">Date</TableHead>
                <TableHead className="text-xs font-bold">Txn Ref</TableHead>
                <TableHead className="text-xs font-bold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {enrollments.map((e) => (
                <TableRow key={e.id} className="hover:bg-slate-50" data-testid={`enrollment-row-${e.id}`}>
                  <TableCell className="font-bold text-sm text-slate-900">{e.student_name}</TableCell>
                  <TableCell className="text-xs text-slate-600 font-mono">{e.citt_id}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${e.enrollment_type === 'renewal' ? 'bg-violet-100 text-violet-700' : 'bg-blue-100 text-blue-700'}`}>
                      {e.enrollment_type === 'renewal' ? 'Renewal' : 'Fresh'}
                    </span>
                  </TableCell>
                  <TableCell className="text-center font-bold text-sm">{e.num_classes}</TableCell>
                  <TableCell className="text-right font-bold text-sm">{e.currency} {e.amount?.toLocaleString()}</TableCell>
                  <TableCell className="text-xs text-slate-600">{e.payment_method}</TableCell>
                  <TableCell className="text-center text-xs text-slate-500">{e.status !== 'pending' ? (e.exchange_rate || '1') : '—'}</TableCell>
                  <TableCell className="text-right">
                    {e.status !== 'pending' ? (
                      <div>
                        <span className="font-bold text-sm">₹{(e.amount_inr || e.amount)?.toLocaleString()}</span>
                        {(e.refunded_inr > 0) && (
                          <span className="block text-[10px] text-red-500 font-bold">-₹{e.refunded_inr?.toLocaleString()} refund</span>
                        )}
                      </div>
                    ) : '—'}
                  </TableCell>
                  <TableCell className="text-center">{getStatusBadge(e)}</TableCell>
                  <TableCell className="text-xs text-slate-500">{e.payment_date || e.created_at?.split('T')[0]}</TableCell>
                  <TableCell className="text-xs text-slate-500 font-mono">{e.transaction_ref || '—'}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {e.status === 'pending' && (
                        <>
                          <Button size="sm" onClick={() => openMarkPaid(e)} className="bg-emerald-600 hover:bg-emerald-700 text-[10px] font-bold h-7 px-2" data-testid={`mark-paid-${e.id}`}>
                            <CheckCircle className="w-3 h-3 mr-1" /> Mark Paid
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleDelete(e.id)} className="text-red-500 border-red-200 hover:bg-red-50 text-[10px] font-bold h-7 px-2" data-testid={`delete-enrollment-${e.id}`}>
                            <X className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                      {(e.status === 'paid' || e.status === 'partial_refund') && (
                        <>
                          <Button size="sm" onClick={() => handleGenerateInvoice(e)} disabled={generatingInvoice === e.id} className="bg-[#1E3A8A] hover:bg-[#1D4ED8] text-[10px] font-bold h-7 px-2" data-testid={`generate-invoice-${e.id}`}>
                            <FileText className="w-3 h-3 mr-1" /> {generatingInvoice === e.id ? '...' : 'Invoice'}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openEdit(e)} className="text-blue-600 border-blue-200 hover:bg-blue-50 text-[10px] font-bold h-7 px-2" data-testid={`edit-enrollment-${e.id}`}>
                            <Edit className="w-3 h-3 mr-1" /> Edit
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => openRefund(e)} className="text-orange-600 border-orange-200 hover:bg-orange-50 text-[10px] font-bold h-7 px-2" data-testid={`refund-enrollment-${e.id}`}>
                            <RotateCcw className="w-3 h-3 mr-1" /> Refund
                          </Button>
                        </>
                      )}
                      {(e.status !== 'pending') && (
                        <Button size="sm" variant="ghost" onClick={() => openAuditLog(e)} className="text-slate-400 hover:text-slate-600 text-[10px] h-7 px-1.5" data-testid={`audit-log-${e.id}`}>
                          <History className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create Enrollment Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{form.enrollment_type === 'renewal' ? 'Renewal' : 'New Enrollment'}</DialogTitle>
            <DialogDescription>{form.enrollment_type === 'renewal' ? 'Add classes for an existing student' : 'Complete enrollment with student + payment in one step'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {/* Enrollment Type Toggle */}
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => handleEnrollmentTypeChange('fresh')}
                className={`px-4 py-2 rounded-lg text-xs font-bold border-2 transition-all ${form.enrollment_type === 'fresh' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
                data-testid="enrollment-type-fresh">
                <Plus className="w-3.5 h-3.5 inline mr-1" /> New Enrollment
              </button>
              <button onClick={() => handleEnrollmentTypeChange('renewal')}
                className={`px-4 py-2 rounded-lg text-xs font-bold border-2 transition-all ${form.enrollment_type === 'renewal' ? 'border-violet-500 bg-violet-50 text-violet-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
                data-testid="enrollment-type-renewal">
                <RefreshCw className="w-3.5 h-3.5 inline mr-1" /> Renewal
              </button>
            </div>

            {/* Student Selection */}
            <div>
              <Label className="text-xs font-bold">{form.enrollment_type === 'renewal' ? 'Select Existing Student' : 'Select Student'}</Label>
              <Select value={form.student_id} onValueChange={handleStudentSelect}>
                <SelectTrigger className="mt-1" data-testid="student-select"><SelectValue placeholder="Select student..." /></SelectTrigger>
                <SelectContent>
                  {students.map(s => (<SelectItem key={s.id} value={s.id}>{s.citt_id || s.student_id || ''} — {s.name}</SelectItem>))}
                  {form.enrollment_type === 'fresh' && (<SelectItem value="__other__" className="border-t mt-1 pt-1 font-semibold text-blue-600">+ New Student</SelectItem>)}
                </SelectContent>
              </Select>
              {selectedStudent && form.student_id !== '__other__' && (
                <div className="mt-1.5 bg-slate-50 rounded-lg p-2.5 border border-slate-200">
                  <p className="text-xs font-bold text-blue-700">{selectedStudent.name} ({selectedStudent.citt_id || selectedStudent.student_id})</p>
                  {form.enrollment_type === 'renewal' && <p className="text-[10px] text-slate-500">Classes remaining: {selectedStudent.classes_remaining || 0}</p>}
                </div>
              )}
            </div>

            {/* New Student Fields */}
            {form.student_id === '__other__' && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 space-y-2.5">
                <p className="text-xs font-bold text-blue-700">New Student Details</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] font-semibold text-slate-600">Student Name *</Label>
                    <Input value={form.new_student_name} onChange={(e) => setForm({ ...form, new_student_name: e.target.value })} placeholder="e.g. Reyansh Patil" className="mt-0.5 h-8 text-xs" data-testid="new-student-name-input" />
                  </div>
                  <div>
                    <Label className="text-[10px] font-semibold text-slate-600">Parent Name</Label>
                    <Input value={form.parent_name} onChange={(e) => setForm({ ...form, parent_name: e.target.value })} placeholder="e.g. Amit Patil" className="mt-0.5 h-8 text-xs" data-testid="parent-name-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] font-semibold text-slate-600">Email ID</Label>
                    <Input type="email" value={form.new_student_email} onChange={(e) => setForm({ ...form, new_student_email: e.target.value })} placeholder="parent@email.com" className="mt-0.5 h-8 text-xs" data-testid="new-student-email-input" />
                  </div>
                  <div>
                    <Label className="text-[10px] font-semibold text-slate-600">Contact Number</Label>
                    <Input value={form.contact_number} onChange={(e) => setForm({ ...form, contact_number: e.target.value })} placeholder="+91 98765 43210" className="mt-0.5 h-8 text-xs" data-testid="contact-number-input" />
                  </div>
                </div>
              </div>
            )}

            {/* Vertical & Program */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs font-bold">Vertical</Label>
                <Select value={form.vertical} onValueChange={(v) => setForm({ ...form, vertical: v, program: '' })}>
                  <SelectTrigger className="mt-1 h-8 text-xs" data-testid="vertical-select"><SelectValue placeholder="Select vertical..." /></SelectTrigger>
                  <SelectContent>{VERTICALS.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold">Program</Label>
                <Select value={form.program} onValueChange={(v) => setForm({ ...form, program: v })} disabled={!form.vertical}>
                  <SelectTrigger className="mt-1 h-8 text-xs" data-testid="program-select"><SelectValue placeholder={form.vertical ? 'Select...' : 'Select vertical first'} /></SelectTrigger>
                  <SelectContent>{(PROGRAMS[form.vertical] || []).map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {/* Country & Timezone */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs font-bold">Country</Label>
                <Select value={form.country} onValueChange={handleCountryChange}>
                  <SelectTrigger className="mt-1 h-8 text-xs" data-testid="country-select"><SelectValue placeholder="Select country..." /></SelectTrigger>
                  <SelectContent>{COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold">Timezone</Label>
                <Select value={form.timezone} onValueChange={(v) => setForm({ ...form, timezone: v })} disabled={!form.country || availableTimezones.length === 0}>
                  <SelectTrigger className="mt-1 h-8 text-xs" data-testid="timezone-select"><SelectValue placeholder={!form.country ? 'Country first' : 'Select...'} /></SelectTrigger>
                  <SelectContent>{availableTimezones.map(tz => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {/* Payment Section */}
            <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200 space-y-2.5">
              <p className="text-xs font-bold text-emerald-700">Payment Details</p>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-[10px] font-semibold text-slate-600">Classes {form.enrollment_type === 'renewal' ? 'Added' : 'Assigned'}</Label>
                  <Input type="number" min="1" value={form.num_classes} onChange={(e) => setForm({ ...form, num_classes: e.target.value })} className="mt-0.5 h-8 text-xs" data-testid="num-classes-input" />
                </div>
                <div>
                  <Label className="text-[10px] font-semibold text-slate-600">Amount</Label>
                  <Input type="number" min="1" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-0.5 h-8 text-xs" data-testid="amount-input" />
                  {form.currency !== 'INR' && form.amount && (
                    <p className="text-[9px] text-emerald-600 mt-0.5">= INR {(parseFloat(form.amount || 0) * (FX_RATES[form.currency] || 1)).toLocaleString('en-IN')}</p>
                  )}
                </div>
                <div>
                  <Label className="text-[10px] font-semibold text-slate-600">Currency</Label>
                  <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                    <SelectTrigger className="mt-0.5 h-8 text-xs" data-testid="currency-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-[10px] font-semibold text-slate-600">Payment Method</Label>
                  <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                    <SelectTrigger className="mt-0.5 h-8 text-xs" data-testid="payment-method-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>{PAYMENT_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] font-semibold text-slate-600">Transaction ID *</Label>
                  <Input value={form.transaction_ref} onChange={(e) => setForm({ ...form, transaction_ref: e.target.value })} placeholder="TXN-12345" className="mt-0.5 h-8 text-xs" data-testid="transaction-ref-input" />
                </div>
                <div>
                  <Label className="text-[10px] font-semibold text-slate-600">Payment Date</Label>
                  <Input type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} className="mt-0.5 h-8 text-xs" data-testid="payment-date-input" />
                </div>
              </div>
            </div>

            <div className="flex gap-2 pt-1">
              <Button variant="outline" onClick={() => setCreateOpen(false)} className="flex-1 text-xs h-9">Cancel</Button>
              <Button onClick={handleCreate} disabled={saving} className="flex-1 bg-blue-600 hover:bg-blue-700 text-xs font-bold h-9" data-testid="submit-enrollment-btn">
                {saving ? 'Processing...' : form.enrollment_type === 'renewal' ? 'Submit Renewal' : 'Enroll & Record Payment'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mark as Paid Dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Mark as Paid</DialogTitle>
            <DialogDescription>Enter payment details to confirm</DialogDescription>
          </DialogHeader>
          {payTarget && (
            <div className="space-y-4 mt-2">
              <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-200">
                <p className="font-bold text-sm text-slate-900">{payTarget.student_name} ({payTarget.citt_id})</p>
                <p className="text-xs text-slate-600">{payTarget.num_classes} classes — {payTarget.currency} {payTarget.amount?.toLocaleString()}</p>
              </div>
              <div>
                <Label className="text-sm font-bold">Transaction Reference Number</Label>
                <Input value={payForm.transaction_ref} onChange={(e) => setPayForm({ ...payForm, transaction_ref: e.target.value })} placeholder="e.g., TXN-2026-001" className="mt-1" data-testid="txn-ref-input" />
              </div>
              <div>
                <Label className="text-sm font-bold">Payment Date</Label>
                <Input type="date" value={payForm.payment_date} onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })} className="mt-1" data-testid="payment-date-input" />
              </div>
              {!payForm.transaction_ref.trim() && (
                <div className="flex items-center gap-2 text-xs text-red-500">
                  <AlertCircle className="w-3.5 h-3.5" /> Transaction reference is required
                </div>
              )}
              <Button onClick={handleMarkPaid} disabled={saving || !payForm.transaction_ref.trim()} className="w-full bg-emerald-600 hover:bg-emerald-700 font-bold text-sm" data-testid="confirm-paid-btn">
                {saving ? 'Processing...' : 'Confirm Payment'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Enrollment Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Enrollment</DialogTitle>
            <DialogDescription>Modify enrollment details. All changes are audit-logged.</DialogDescription>
          </DialogHeader>
          {editTarget && (
            <div className="space-y-4 mt-2">
              <div className="bg-blue-50 rounded-xl p-3 border border-blue-200">
                <p className="font-bold text-sm text-slate-900">{editTarget.student_name} ({editTarget.citt_id})</p>
                <p className="text-xs text-slate-600">Current: {editTarget.num_classes} classes — {editTarget.currency} {editTarget.amount?.toLocaleString()}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-bold">Number of Classes</Label>
                  <Input type="number" min="1" value={editForm.num_classes} onChange={(e) => setEditForm({ ...editForm, num_classes: e.target.value })} className="mt-1" data-testid="edit-num-classes" />
                </div>
                <div>
                  <Label className="text-sm font-bold">Currency</Label>
                  <Select value={editForm.currency} onValueChange={(v) => setEditForm({ ...editForm, currency: v })}>
                    <SelectTrigger className="mt-1" data-testid="edit-currency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-bold">Amount</Label>
                  <Input type="number" min="1" step="0.01" value={editForm.amount} onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })} className="mt-1" data-testid="edit-amount" />
                </div>
                <div>
                  <Label className="text-sm font-bold">Payment Method</Label>
                  <Select value={editForm.payment_method} onValueChange={(v) => setEditForm({ ...editForm, payment_method: v })}>
                    <SelectTrigger className="mt-1" data-testid="edit-payment-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-sm font-bold">Transaction Ref</Label>
                <Input value={editForm.transaction_ref} onChange={(e) => setEditForm({ ...editForm, transaction_ref: e.target.value })} className="mt-1" data-testid="edit-txn-ref" />
              </div>
              {editTarget.status === 'paid' && editForm.amount && editForm.currency && (
                <div className="bg-slate-50 rounded-lg p-2 text-xs text-slate-600">
                  New INR Value: ₹{(parseFloat(editForm.amount) * (FX_RATES[editForm.currency] || 1)).toLocaleString()} (Rate: {FX_RATES[editForm.currency] || 1})
                </div>
              )}
              <div>
                <Label className="text-sm font-bold text-red-600">Reason for Edit *</Label>
                <Textarea value={editForm.reason} onChange={(e) => setEditForm({ ...editForm, reason: e.target.value })} placeholder="Why is this enrollment being edited?" className="mt-1" rows={2} data-testid="edit-reason" />
              </div>
              <div className="flex gap-2 pt-1">
                <Button variant="outline" onClick={() => setEditOpen(false)} className="flex-1 text-sm">Cancel</Button>
                <Button onClick={handleEdit} disabled={saving || !editForm.reason.trim()} className="flex-1 bg-blue-600 hover:bg-blue-700 text-sm font-bold" data-testid="save-edit-btn">
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Refund Dialog */}
      <Dialog open={refundOpen} onOpenChange={setRefundOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Process Refund</DialogTitle>
            <DialogDescription>Refund will adjust revenue and deduct class credits</DialogDescription>
          </DialogHeader>
          {refundTarget && (
            <div className="space-y-4 mt-2">
              <div className="bg-orange-50 rounded-xl p-3 border border-orange-200">
                <p className="font-bold text-sm text-slate-900">{refundTarget.student_name} ({refundTarget.citt_id})</p>
                <p className="text-xs text-slate-600">{refundTarget.num_classes} classes — ₹{(refundTarget.amount_inr || refundTarget.amount)?.toLocaleString()} INR</p>
                <p className="text-xs text-slate-500">Original: {refundTarget.currency} {refundTarget.amount?.toLocaleString()} @ rate {refundTarget.exchange_rate || 1}</p>
                {refundTarget.refunded_inr > 0 && (
                  <p className="text-xs text-red-600 font-bold mt-1">Already refunded: ₹{refundTarget.refunded_inr?.toLocaleString()}</p>
                )}
              </div>
              <div>
                <Label className="text-sm font-bold">Refund Type</Label>
                <Select value={refundForm.refund_type} onValueChange={(v) => setRefundForm({ ...refundForm, refund_type: v })}>
                  <SelectTrigger className="mt-1" data-testid="refund-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full">Full Refund</SelectItem>
                    <SelectItem value="partial">Partial Refund</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {refundForm.refund_type === 'partial' && (
                <div>
                  <Label className="text-sm font-bold">Refund Amount ({refundTarget.currency})</Label>
                  <Input type="number" min="1" step="0.01" value={refundForm.refund_amount} onChange={(e) => setRefundForm({ ...refundForm, refund_amount: e.target.value })} placeholder={`Max: ${refundTarget.amount}`} className="mt-1" data-testid="refund-amount-input" />
                  {refundForm.refund_amount && (
                    <p className="text-xs text-slate-500 mt-1">
                      ≈ ₹{(parseFloat(refundForm.refund_amount) * (refundTarget.exchange_rate || 1)).toLocaleString()} INR (using original rate: {refundTarget.exchange_rate || 1})
                    </p>
                  )}
                </div>
              )}
              <div>
                <Label className="text-sm font-bold text-red-600">Reason for Refund *</Label>
                <Textarea value={refundForm.reason} onChange={(e) => setRefundForm({ ...refundForm, reason: e.target.value })} placeholder="Why is this refund being processed?" className="mt-1" rows={2} data-testid="refund-reason" />
              </div>
              <div className="flex gap-2 pt-1">
                <Button variant="outline" onClick={() => setRefundOpen(false)} className="flex-1 text-sm">Cancel</Button>
                <Button onClick={handleRefund} disabled={saving || !refundForm.reason.trim()} className="flex-1 bg-orange-600 hover:bg-orange-700 text-white text-sm font-bold" data-testid="process-refund-btn">
                  {saving ? 'Processing...' : 'Process Refund'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Audit Log Dialog */}
      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Audit Log</DialogTitle>
            <DialogDescription>{auditTarget?.student_name} — {auditTarget?.citt_id}</DialogDescription>
          </DialogHeader>
          {auditLogs.length === 0 ? (
            <p className="text-sm text-slate-500 py-4 text-center">No audit entries for this enrollment</p>
          ) : (
            <div className="space-y-3 mt-2">
              {auditLogs.map((log) => (
                <div key={log.id} className={`rounded-lg p-3 border text-sm ${log.action === 'refund' ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded-full ${log.action === 'refund' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                      {log.action}
                    </span>
                    <span className="text-xs text-slate-500">{new Date(log.created_at).toLocaleString()}</span>
                  </div>
                  <p className="text-xs text-slate-600 mb-1">By: {log.performed_by_name}</p>
                  {log.reason && <p className="text-xs text-slate-700 font-medium">Reason: {log.reason}</p>}
                  {log.action === 'refund' && (
                    <p className="text-xs text-red-700 font-bold mt-1">
                      {log.refund_type === 'full' ? 'Full' : 'Partial'} refund: ₹{log.refund_amount_inr?.toLocaleString()} — {log.refund_classes} classes deducted
                    </p>
                  )}
                  {log.action === 'edit' && log.before && (
                    <div className="text-xs text-slate-600 mt-1 space-y-0.5">
                      {Object.keys(log.after || {}).map(key => (
                        <p key={key}><span className="font-bold">{key}:</span> {String(log.before[key])} → {String(log.after[key])}</p>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EnrollmentManager;
